import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { createColumnHelper } from '@tanstack/react-table';

import { CenterAlignedCell, DollarValueCell } from 'components/Table';
import { BidSheetActions, BidSheetRow } from './useBidSheetState';
import CheckboxCell from '../components/CheckboxCell/CheckboxCell';
import RangeIndicatedCell from '../components/RangeIndicatedCell/RangeIndicatedCell';

type ModifiedAccessorFn<T extends keyof BidSheetRow> = (row: BidSheetRow) => BidSheetRow[T] | null;

const dollarValueFormatter = (value: number | null) => {
  if (value === null || value === undefined) {
    return '';
  }

  return Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
};

const numberValueFormatter = (value: number | null) => {
  if (value === null || value === undefined) {
    return '';
  }

  return Intl.NumberFormat('en-US').format(value);
};

const modifiedRowAccessor =
  <T extends keyof BidSheetRow>(key: T): ModifiedAccessorFn<T> =>
  (row) => {
    if (!row.modified) {
      return null;
    }

    return row[key];
  };

const useColumns = (actions: BidSheetActions) => {
  const { t } = useTranslation();

  return useMemo(() => {
    const columnHelper = createColumnHelper<BidSheetRow>();

    return [
      columnHelper.accessor('selected', {
        cell: (props) => <CheckboxCell {...props} toggle={() => actions.toggleBidSelection(props.row.original.id)} />,
        header: t('pages.bidSheet.selected'),
        size: 10,
        meta: {
          headerClassName: 'center',
        },
      }),
      columnHelper.accessor('issuanceYear', {
        cell: CenterAlignedCell,
        header: t('pages.bidSheet.issuanceYear'),
        size: 50,
        meta: {
          headerClassName: 'center',
        },
      }),
      columnHelper.accessor('issuanceName', {
        header: t('pages.bidSheet.issuanceName'),
        size: 100,
      }),
      columnHelper.accessor(modifiedRowAccessor('volume'), {
        id: 'volume',
        header: t('pages.bidSheet.volume'),
        cell: RangeIndicatedCell,
        size: 50,
        meta: {
          headerClassName: 'center',
          formatter: numberValueFormatter,
        },
      }),
      columnHelper.accessor(modifiedRowAccessor('bidPrice'), {
        id: 'bidPrice',
        header: t('pages.bidSheet.bidPrice'),
        cell: RangeIndicatedCell,
        size: 50,
        meta: {
          headerClassName: 'center',
          formatter: dollarValueFormatter,
        },
      }),
      columnHelper.accessor(modifiedRowAccessor('subTotal'), {
        id: 'subTotal',
        header: t('pages.bidSheet.subTotal'),
        // @ts-ignore
        cell: DollarValueCell,
        size: 50,
        meta: {
          headerClassName: 'center',
        },
      }),
      columnHelper.accessor(modifiedRowAccessor('downPayment'), {
        id: 'downPayment',
        header: t('pages.bidSheet.downPayment'),
        cell: RangeIndicatedCell,
        size: 50,
        meta: {
          headerClassName: 'center',
          formatter: dollarValueFormatter,
        },
      }),
      columnHelper.accessor(modifiedRowAccessor('discount'), {
        id: 'discount',
        header: t('pages.bidSheet.discount'),
        size: 50,
        cell: RangeIndicatedCell,
        meta: {
          headerClassName: 'center',
          formatter: (value) => (value === null || value === undefined || isNaN(value) ? '-' : `${value} %`),
        },
      }),
      columnHelper.accessor(modifiedRowAccessor('total'), {
        id: 'total',
        header: t('pages.bidSheet.total'),
        // @ts-ignore
        cell: DollarValueCell,
        size: 50,
        meta: {
          headerClassName: 'center',
        },
      }),
      columnHelper.accessor(modifiedRowAccessor('averagePrice'), {
        id: 'averagePrice',
        header: t('pages.bidSheet.avgPrice'),
        // @ts-ignore
        cell: DollarValueCell,
        size: 50,
        meta: {
          headerClassName: 'center',
        },
      }),
    ];
  }, [t, actions]);
};

export default useColumns;
